import PropTypes from 'prop-types';
import { useCallback, useRef, useEffect } from 'react';
import { FaTimes } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';

import { openDrawer } from '../../features/drawersSlice';
import {
    getWidgetPosition,
    applyImportantStyles,
    makeStatsRequestThrottled,
    saveWidgetStateToLS,
} from '../../lib';
import { WIDGET_STATS_SETTINGS } from '../../config';
import { getBookingWidgetMultipropertyData } from 'features/multiproperty/MultipropertySlice';
import { Paginate } from 'components/common/paginate';
// import { Stars } from 'components/common/stars';
import './MultipropertyCarouselWidget.css';
import { SmallTextMultipropertyWidget } from 'components/smallTextMultipropertyWidget';
import { DesktopBookingWidget } from 'components/bookingWidget';

const MultipropertyCarouselWidget = ({ toggleWidget, mobile, extraClass }) => {
    const dispatch = useDispatch();

    const data = useSelector((state) => state.miniBookingWidget?.data) || {};
    const showOnlySmallMutlipropertyButton = useSelector(
        (state) => state.miniBookingWidget?.showOnlySmallMutlipropertyButton
    );
    const showSingleWidgetMultiproperty = useSelector(
        (state) => state.miniBookingWidget?.showSingleWidgetMultiproperty
    );
    const hidden = useSelector((state) => state.miniBookingWidget.hidden);

    const {
        Behavior: clickAction,
        colors,
        closeBtn: closeWidgetBtn,
        propertyID: propertyId,
        showMultipropertySmallBtn,
    } = data;

    const multipropertyUrl = useSelector(
        (state) => state.miniBookingWidget?.data?.multipropertyUrl
    );
    const widgetPosition = useSelector(
        (state) => state.miniBookingWidget.widgetPosition
    );
    const multipropertyPropeties =
        useSelector((state) => state.multiproperty.multipropertyProperties) ||
        [];

    const closeBtnCntRef = useRef(null);

    const showOnlySmallMobileMultiproperty = useSelector(
        (state) =>
            state.miniBookingWidget.data?.showOnlySmallMobileMultiproperty
    );

    useEffect(() => {
        if (multipropertyUrl) {
            dispatch(
                getBookingWidgetMultipropertyData({
                    multipropertyUrl,
                })
            );
        }
    }, [multipropertyUrl]);

    const handleWidgetContentonMouseOver = () => {
        closeBtnCntRef.current.classList.add('visible');
    };

    const handleWidgetContentonMouseLeave = (event) => {
        closeBtnCntRef.current.classList.remove('visible');
    };

    const isOpen = useSelector((state) => state.miniBookingWidget.open);
    const isDismissed = useSelector(
        (state) => state.miniBookingWidget.isDismissed
    );

    const intervalID = useSelector((state) => state.multiproperty.intervalID);

    const onClick = useCallback(
        (event) => {
            // Click handler should not run paginate buttons are clicked
            const targetElem = event.target;
            const targetElemRole = targetElem.getAttribute('role');
            const targetElemAriaLabel = targetElem.getAttribute('aria-label');

            if (
                targetElemRole === 'button' &&
                (targetElemAriaLabel?.startsWith('Page') ||
                    targetElemAriaLabel?.startsWith('Next page') ||
                    targetElemAriaLabel?.startsWith('Previous page'))
            ) {
                return;
            }
            if (intervalID) {
                clearInterval(intervalID);
            }
            if (mobile) {
                dispatch(openDrawer({ drawerId: 'MobileMultipropertyDrawer' }));
            } else {
                dispatch(openDrawer({ drawerId: 'BigBookingWidgetDrawer' }));
            }

            makeStatsRequestThrottled(
                WIDGET_STATS_SETTINGS.actions.click,
                propertyId
            );
        },
        [clickAction, intervalID]
    );

    const inlineStyles = { display: hidden ? 'none' : 'block' };

    const widgetPositionStyles = getWidgetPosition(widgetPosition);
    let isVisible = '';
    if (mobile) {
        isVisible = 'visible';
    }

    if (data.pluginZIndex) {
        inlineStyles.zIndex = data.pluginZIndex;
    }

    if (
        isDismissed ||
        !multipropertyPropeties ||
        multipropertyPropeties?.length === 0
    ) {
        return null;
    }

    if (!isOpen || showSingleWidgetMultiproperty) {
        let lowestPriceProperty = null;

        const validProperties = multipropertyPropeties.filter((property) => {
            const propertyPrice = parseFloat(property.ourPrice?.price);
            return propertyPrice > 0;
        });

        if (validProperties.length > 0) {
            validProperties.forEach((property) => {
                const propertyPrice = parseFloat(property.ourPrice.price);

                if (
                    lowestPriceProperty === null ||
                    propertyPrice <
                        parseFloat(lowestPriceProperty.ourPrice.price)
                ) {
                    lowestPriceProperty = property;
                }
            });

            if (lowestPriceProperty) {
                return (
                    <DesktopBookingWidget
                        onClickParam={onClick}
                        showWidget={() => {}}
                        toggleWidget={toggleWidget}
                        singleProperty={lowestPriceProperty}
                    />
                );
            }
        }
    }
    // we render it here if is mobile and the option is true or is dismissed
    if (
        !isOpen ||
        showOnlySmallMobileMultiproperty ||
        showOnlySmallMutlipropertyButton
    ) {
        return (
            <SmallTextMultipropertyWidget
                position={widgetPosition.position.split('-')[1]}
                mobile={mobile}
            />
        );
    }

    return (
        <>
            {isOpen && (
                <div
                    onMouseOver={handleWidgetContentonMouseOver}
                    onMouseLeave={handleWidgetContentonMouseLeave}
                    className={`ab-multiproperty-booking-widget ${extraClass}`}
                >
                    <div
                        className="ab-multiproperty-widget"
                        style={{
                            ...inlineStyles,
                        }}
                        ref={(el) => {
                            applyImportantStyles(el, widgetPositionStyles);
                        }}
                        onClick={onClick}
                    >
                        <div
                            className={`ab-desktop-widget-close-btn ab-multiproperty-close-btn ${isVisible}`}
                            onClick={(event) => {
                                event.stopPropagation();
                                toggleWidget();
                                saveWidgetStateToLS({
                                    lastMultipropertyCloseTimestamp: Date.now(),
                                });
                            }}
                            // style={{ display: 'none' }}
                            ref={closeBtnCntRef}
                        >
                            <button
                                className="ab-desktop-widget-close-btn--button"
                                ref={(el) => {
                                    applyImportantStyles(el, {
                                        'background-color':
                                            colors.secondaryBackgroundColor,
                                        'border-radius': '16px',
                                        color: colors.primaryTextColor,
                                    });
                                }}
                            >
                                <span
                                    style={{
                                        color: colors.primaryTextColor,
                                    }}
                                >
                                    {closeWidgetBtn?.text}
                                </span>
                                <span
                                    style={{
                                        color: colors.primaryTextColor,
                                    }}
                                >
                                    <FaTimes />
                                </span>
                            </button>
                        </div>
                        <div className="ab-multiproperty-bottom-el">
                            {multipropertyPropeties?.length > 0 && (
                                <div className="ab-multiproperty-pager">
                                    <Paginate items={multipropertyPropeties} />
                                </div>
                            )}
                            <div
                                className="ab-flex-class"
                                style={{ alignItems: 'end' }}
                            >
                                <div className="ab-multiproperty-widget-multiproperty-button">
                                    <button
                                        ref={(el) => {
                                            applyImportantStyles(el, {
                                                'background-color':
                                                    colors.secondaryBackgroundColor,
                                                color: colors.primaryTextColor,
                                            });
                                        }}
                                    >
                                        View All
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="ab-multiproperty-widget-footer-position">
                            <div
                                style={{
                                    display: 'block',
                                }}
                            >
                                <div className="ab-multiproperty-widget-footer">
                                    <div>
                                        <a
                                            href="https://www.ratestrip.com/"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <img
                                                src="https://static.abouthotelier.com/img/ratestrip_logo.svg"
                                                alt=""
                                                style={{
                                                    width: '70px',
                                                }}
                                            />
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {!isOpen && showMultipropertySmallBtn && (
                <SmallTextMultipropertyWidget
                    position={widgetPosition.position.split('-')[1]}
                    mobile={mobile}
                />
            )}
        </>
    );
};

MultipropertyCarouselWidget.propTypes = {
    price: PropTypes.string,
    text: PropTypes.string,
    rateType: PropTypes.string,
    currencySymbol: PropTypes.string,
    className: PropTypes.string,
    onClick: PropTypes.func,
    icon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    link: PropTypes.bool,
    url: PropTypes.string,
    toggleWidget: PropTypes.func,
    mobile: PropTypes.bool,
    extraClass: PropTypes.string,
};

MultipropertyCarouselWidget.defaultProps = {
    className: '',
};

export default MultipropertyCarouselWidget;
